import { useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../auth/AuthContext';
import { Logo } from '../components/brand/Logo';
import { SimulationsTab } from '../components/console/SimulationsTab';
import { AssetsTab } from '../components/console/AssetsTab';
import { BillingTab } from '../components/console/BillingTab';
import { ApiKeysTab } from '../components/console/ApiKeysTab';
import { useSimulationHistory } from '../hooks/useSimulationHistory';
import { useAssets } from '../hooks/useAssets';

const NAV = [
  { id: 'simulations', label: 'Simulations', icon: 'inventory_2'  },
  { id: 'assets',      label: 'Assets',      icon: 'category'     },
  { id: 'billing',     label: 'Billing',     icon: 'receipt_long' },
  { id: 'api-keys',    label: 'API Keys',    icon: 'key'          },
];

export function ConsolePage() {
  const { isAuthenticated, ready, user, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const sharedSimId = searchParams.get('id') || null;
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabParam || 'simulations');
  const historyApi = useSimulationHistory();
  const assetHook  = useAssets();

  if (!ready || (isAuthenticated && historyApi.loading)) return (
    <div className="flex h-screen w-full items-center justify-center bg-[#060b1a]">
      <span className="material-symbols-outlined animate-spin text-4xl text-primary">progress_activity</span>
    </div>
  );
  if (!isAuthenticated) {
    const dest = `/console${sharedSimId ? `?id=${sharedSimId}` : ''}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(dest)}`} replace />;
  }

  return (
    <div className="flex h-screen overflow-hidden bg-[#060b1a] text-white">
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>

      {/* ── Sidebar ─────────────────────────────────────────────────────────── */}
      <aside className="flex w-56 shrink-0 flex-col border-r border-white/10 bg-slate-950/80">

        {/* Logo */}
        <div className="border-b border-white/10 px-4 py-5">
          <Link to="/" className="flex items-center gap-0.5 group w-fit">
            <Logo className="h-5 w-auto transition-transform group-hover:scale-110" />
            <span className="translate-y-0.5 text-sm font-black tracking-wide text-white group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-primary group-hover:via-blue-400 group-hover:to-cyan-400 group-hover:bg-clip-text transition-all">
              euteuristix
            </span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-0.5 px-3 py-4">
          {NAV.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition
                ${activeTab === item.id
                  ? 'bg-primary/15 text-primary'
                  : 'text-slate-400 hover:bg-white/5 hover:text-white'}`}
            >
              <span className="material-symbols-outlined text-[18px]">{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>

        {/* User section */}
        <div className="border-t border-white/10 px-4 py-4">
          <p className="text-xs font-semibold text-white truncate">{user?.name || user?.email}</p>
          <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          <button
            onClick={logout}
            className="mt-3 flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition"
          >
            <span className="material-symbols-outlined text-[16px]">logout</span>
            Sign out
          </button>
        </div>
      </aside>

      {/* ── Main content — all tabs stay mounted; hidden via CSS to preserve state ── */}
      <main className="flex-1 overflow-y-auto">
        <div className={activeTab === 'simulations' ? '' : 'hidden'}>
          <SimulationsTab historyApi={historyApi} sharedSimId={sharedSimId} assetHook={assetHook} />
        </div>
        <div className={activeTab === 'assets' ? '' : 'hidden'}>
          <AssetsTab assetHook={assetHook} />
        </div>
        <div className={activeTab === 'billing' ? '' : 'hidden'}>
          <BillingTab historyApi={historyApi} />
        </div>
        <div className={activeTab === 'api-keys' ? '' : 'hidden'}>
          <ApiKeysTab />
        </div>
      </main>

    </div>
  );
}
